import React, { useEffect, useState } from 'react';
import { Card, Tabs, Table, Tag, message, Space, Button, Popconfirm, Modal, Descriptions, Typography } from 'antd';
import { useCanDelete } from '../hooks/useCanDelete';
import dayjs from 'dayjs';
import {
  fetchServiceOrderNotifications,
  cancelServiceOrderNotification,
  deleteServiceOrderNotification,
  sendBulkUpcomingNotifications,
  fetchServiceOrder,
  type ServiceOrderNotificationDto,
  type ServiceOrderDto,
  type ServiceOrderLineDto,
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';

const STATUS = {
  pending: 1,
  sent: 2,
  cancelled: 3,
  postponed: 4,
};

const ServiceOrderNotifications: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [activeTab, setActiveTab] = useState('pending');
  const [items, setItems] = useState<ServiceOrderNotificationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailOrder, setDetailOrder] = useState<ServiceOrderDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadItems = async (tabKey = activeTab) => {
    try {
      setLoading(true);
      const params: { status?: number; fromDate?: string; toDate?: string } = {};
      const now = dayjs();

      if (tabKey === 'upcoming') {
        params.status = STATUS.pending;
        params.fromDate = now.toISOString();
        params.toDate = now.add(30, 'day').toISOString();
      } else if (tabKey === 'pending') {
        params.status = STATUS.pending;
      } else if (tabKey === 'sent') {
        params.status = STATUS.sent;
      } else if (tabKey === 'cancelled') {
        params.status = STATUS.cancelled;
      } else if (tabKey === 'postponed') {
        params.status = STATUS.postponed;
      }

      const data = await fetchServiceOrderNotifications(params);
      setItems(data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [activeTab]);

  const handleCancel = async (id: number) => {
    try {
      await cancelServiceOrderNotification(id);
      message.success('Bildirim iptal edildi');
      loadItems();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'İptal işlemi başarısız');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteServiceOrderNotification(id);
      message.success('Bildirim silindi');
      loadItems();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Silme işlemi başarısız');
    }
  };

  const handleShowOrderDetail = async (serviceOrderId: number) => {
    try {
      setDetailLoading(true);
      setDetailModalVisible(true);
      const data = await fetchServiceOrder(serviceOrderId);
      setDetailOrder(data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Servis fişi yüklenirken hata oluştu');
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBulkSend = async () => {
    try {
      setSendingBulk(true);
      const result = await sendBulkUpcomingNotifications();
      const msg = `Gönderim tamamlandı: ${result.sent} gönderildi, ${result.failed} hata, ${result.skipped} atlandı.`;
      if (result.failed > 0 && result.errors?.length) {
        message.warning(msg);
        console.warn('Bildirim hataları:', result.errors);
      } else {
        message.success(msg);
      }
      loadItems();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Toplu gönderim başarısız');
    } finally {
      setSendingBulk(false);
    }
  };

  const columns = [
      {
        title: 'Durum',
        dataIndex: 'statusText',
        key: 'statusText',
        render: (_: string, record: ServiceOrderNotificationDto) => {
          const color =
            record.status === STATUS.sent
              ? 'green'
              : record.status === STATUS.cancelled
                ? 'red'
                : record.status === STATUS.postponed
                  ? 'orange'
                  : 'blue';
          return <Tag color={color}>{record.statusText}</Tag>;
        },
      },
      {
        title: 'Hatırlatma Tarihi',
        dataIndex: 'reminderDate',
        key: 'reminderDate',
        render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
      },
      {
        title: 'Süre (Ay)',
        dataIndex: 'reminderMonths',
        key: 'reminderMonths',
      },
      {
        title: 'Müşteri',
        dataIndex: 'customerName',
        key: 'customerName',
      },
      {
        title: 'Araç',
        dataIndex: 'vehiclePlate',
        key: 'vehiclePlate',
      },
      {
        title: 'Servis Fişi',
        dataIndex: 'serviceOrderNo',
        key: 'serviceOrderNo',
        render: (no: string, record: ServiceOrderNotificationDto) => (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleShowOrderDetail(record.serviceOrderId)}>
            {no}
          </Button>
        ),
      },
      {
        title: 'Satır No',
        dataIndex: 'serviceOrderLineNo',
        key: 'serviceOrderLineNo',
        render: (value: number | null) => (value ? value : '-'),
      },
      {
        title: 'Fiş Bazında',
        dataIndex: 'isFicheNotification',
        key: 'isFicheNotification',
        render: (value: boolean) => (value ? 'Evet' : 'Hayır'),
      },
      {
        title: 'Oluşturulma',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm'),
      },
      {
        title: 'Gönderim',
        dataIndex: 'sentAt',
        key: 'sentAt',
        render: (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        title: 'İşlemler',
        key: 'actions',
        render: (_: unknown, record: ServiceOrderNotificationDto) => (
          <Space>
            <Popconfirm
              title="Bildirim iptal edilsin mi?"
              okText="Evet"
              cancelText="Hayır"
              onConfirm={() => handleCancel(record.id)}
              disabled={record.status === STATUS.cancelled}
            >
              <Button size="small" disabled={record.status === STATUS.cancelled}>
                İptal Et
              </Button>
            </Popconfirm>
            {canDelete && (
            <Popconfirm
              title="Bildirim silinsin mi?"
              okText="Evet"
              cancelText="Hayır"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button size="small" danger>
                Sil
              </Button>
            </Popconfirm>
            )}
          </Space>
        ),
      },
    ];

  return (
    <Card
      title="Bildirimler"
      extra={
        <Space>
          {activeTab === 'upcoming' && (
            <Popconfirm
              title="Yaklaşan 30 gündeki tüm bekleyen bildirimlere SMS gönderilsin mi?"
              okText="Gönder"
              cancelText="İptal"
              onConfirm={handleBulkSend}
            >
              <Button type="primary" loading={sendingBulk}>
                Toplu bildirim gönder
              </Button>
            </Popconfirm>
          )}
          <Button onClick={() => loadItems()} loading={loading}>
            Yenile
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          { key: 'pending', label: 'Bekleyen' },
          { key: 'upcoming', label: 'Yaklaşan (30 gün)' },
          { key: 'sent', label: 'Gönderilen' },
          { key: 'cancelled', label: 'İptal' },
          { key: 'postponed', label: 'Ertelendi' },
        ]}
      />
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        scroll={{ x: 600 }}
        pagination={{ pageSize: 20, showSizeChanger: !isMobile }}
      />

      <Modal
        title={`Servis Fişi Detayı ${detailOrder ? `- ${detailOrder.orderNo}` : ''}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setDetailOrder(null);
        }}
        footer={
          <Button onClick={() => { setDetailModalVisible(false); setDetailOrder(null); }}>
            Kapat
          </Button>
        }
        width={isMobile ? '100%' : 800}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        {detailLoading && <div style={{ padding: 24, textAlign: 'center' }}>Yükleniyor...</div>}
        {!detailLoading && detailOrder && (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Fiş No">{detailOrder.orderNo}</Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={
                  detailOrder.status === 1 ? 'blue' : detailOrder.status === 2 ? 'orange' :
                  detailOrder.status === 3 ? 'purple' : detailOrder.status === 4 ? 'green' : 'cyan'
                }>{detailOrder.statusText}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Müşteri">{detailOrder.customerName}</Descriptions.Item>
              <Descriptions.Item label="Araç">{detailOrder.vehiclePlate} {detailOrder.vehicleInfo ? `(${detailOrder.vehicleInfo})` : ''}</Descriptions.Item>
              <Descriptions.Item label="Tarih">{dayjs(detailOrder.orderDate).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Toplam Tutar">
                <Typography.Text strong>{detailOrder.totalAmount?.toFixed(2) ?? '0.00'} ₺</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              dataSource={detailOrder.lines || []}
              rowKey="id"
              pagination={false}
              columns={[
                { title: 'Satır', dataIndex: 'lineNo', key: 'lineNo', width: 60 },
                { title: 'Tip', dataIndex: 'itemTypeText', key: 'itemTypeText', width: 90, render: (t: string, r: ServiceOrderLineDto) => <Tag color={r.itemType === 1 ? 'blue' : 'green'}>{t}</Tag> },
                { title: 'Kalem', key: 'itemName', render: (_: unknown, r: ServiceOrderLineDto) => r.serviceItemName || `${r.productCode || ''} ${r.productName || ''}`.trim() || '-' },
                { title: 'Miktar', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right' as const },
                { title: 'Birim Fiyat', dataIndex: 'unitPrice', key: 'unitPrice', width: 100, align: 'right' as const, render: (p: number) => `${(p ?? 0).toFixed(2)} ₺` },
                { title: 'Toplam', dataIndex: 'totalPrice', key: 'totalPrice', width: 100, align: 'right' as const, render: (p: number) => <strong>{(p ?? 0).toFixed(2)} ₺</strong> },
              ]}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default ServiceOrderNotifications;
